import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { useInView } from 'react-intersection-observer';
import './Focus.css';

interface FocusArea {
  id: number;
  title: string;
  description: string;
  image: string;
  image_url: string;
  order: number;
  is_active: boolean;
}

const Focus: React.FC = () => {
  const [focusAreas, setFocusAreas] = useState<FocusArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // triggerOnce: false -> animation replays every time you scroll it into view
  const { ref: headerRef, inView: headerInView } = useInView({ threshold: 0.2, triggerOnce: false });
  const { ref: cardsRef, inView: cardsInView } = useInView({ threshold: 0.1, triggerOnce: false });

  useEffect(() => {
    fetchFocusAreas();
  }, []);

  const fetchFocusAreas = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl('/api/focus-areas'));
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        // Filter only active focus areas for the public website
        const activeFocusAreas = result.data.filter((area: FocusArea) => area.is_active);
        setFocusAreas(activeFocusAreas);
      } else {
        setError('API returned unsuccessful response');
      }
    } catch (err) {
      setError(`Failed to load focus areas: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Fetch error:', err);
      
      // Fallback to static data if API fails
      setFocusAreas([
        {
          id: 1,
          title: 'Quality Education',
          description: 'Providing quality education and literacy programs to empower individuals with knowledge and skills for a better future.',
          image: 'education.jpg',
          image_url: '/images/education.jpg',
          order: 1,
          is_active: true
        },
        {
          id: 2,
          title: 'Enhance Health Services',
          description: 'Improving healthcare access, nutrition, and wellness programs to build healthier communities across Afghanistan.',
          image: 'health.jpg',
          image_url: '/images/health.jpg',
          order: 2,
          is_active: true
        },
        {
          id: 3,
          title: 'Empower Women and children',
          description: 'Promoting gender equality through education, economic opportunities, and leadership development for women and girls.',
          image: 'woman.jpg',
          image_url: '/images/woman.jpg',
          order: 3,
          is_active: true
        },
        {
          id: 4,
          title: 'AI & Digital Skills Oppurtunities',
          description: 'Empowering women and youth with Artificial Intelligence and digital skills to create sustainable careers, freelancing opportunities, and technology-driven solutions.',
          image: 'freelancing.jpg',
          image_url: '/images/freelancing.jpg',
          order: 4,
          is_active: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="focus-area">
        <div className="container">
          <div className="section__header p-0 m-0">
            <h2 className="title-animation">What We Do</h2>
          </div>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading focus areas...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && focusAreas.length === 0) {
    return (
      <section className="focus-area">
        <div className="container">
          <div className="section__header p-0 m-0">
            <h2 className="title-animation">Focus Areas</h2>
          </div>
          <div className="error-state">
            <p className="error-message">⚠️ {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="focus-area">
      <div className="container">
        <div
          ref={headerRef}
          className={`section__header p-0 m-0 ${headerInView ? 'animate-header' : ''}`}
        >
          <h2 className="title-animation scroll-title">Focus Areas</h2>
        </div>
        
        <div className="focus-wrapper">
          <div className="focus-inner">
            <div className="section__header-secondary">
              <div className="row gutter-30 align-items-center">
                <div className="col-12 col-lg-8 col-xxl-7">
                  <div className="section__header mb-0">
                    <h2 className="title-animation"></h2>
                  </div>
                </div>
                <div className="col-12 col-lg-4 col-xxl-5">
                  {/* Optional content can go here */}
                </div>
              </div>
            </div>
            
            <div className="focus-grid">
              <div ref={cardsRef} className="row gutter-60">
                {focusAreas.map((area, idx) => (
                  <div
                    key={area.id}
                    className={`col-12 col-md-6 col-xxl-3 focus-col ${cardsInView ? 'animate-card' : ''}`}
                    style={{ transitionDelay: cardsInView ? `${Math.min(idx, 5) * 80}ms` : '0ms' }}
                  >
                    <div className="focus-single">
                      <div className="focus-thumb">
                        <div className="thumb">
                          {/* Use image_url from API, fallback to image */}
                          {/* <img 
                            src={area.image_url || `/images/${area.image}`} 
                            alt={area.title}
                            className="focus-image"
                            loading="lazy"
                            onError={(e) => {
                              // Fallback to placeholder if image fails to load
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300';
                            }}
                          /> */}

                              <img 
                                src={area.image_url || `/storage/${area.image}`} 
                                alt={area.title}
                                className="focus-image"
                                loading="lazy"
                                onError={(e) => {
                                  // Use a working placeholder service
                                  (e.target as HTMLImageElement).src = 'https://placehold.co/400x300/2563eb/white?text=' + encodeURIComponent(area.title.charAt(0));
                                }}
                              />
                        </div>
                      </div>
                      <div className="focus-content">
                        <h6>{area.title}</h6>
                        <p>{area.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        
      </div>
      
      <div className="focus-bg">
        <div className="focus-bg-pattern">
          {/* Background pattern elements */}
        </div>
        <div className="focus-bg-shape">
          {/* Background shape elements */}
        </div>
      </div>
    </section>
  );
};

export default Focus;